import React, { useEffect, useRef, useState } from 'react';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { FIX } from '../../lib/documentStatus';

/**
 * The single card that reports a problem with an uploaded document.
 *
 * It renders a descriptor from buildDocumentStatus and decides nothing itself — the
 * whole point of splitting the two is that the wording and the button can no longer
 * disagree with the check that produced them.
 *
 * Three things here are deliberate rather than decorative:
 *
 *  - It replaces a toast. A toast covered the top-right of the screen on a phone, timed
 *    out while the candidate was reading it, and sat nowhere near the thing it was about.
 *    This card renders in the flow, under the document it concerns, and stays until the
 *    problem does. `role="alert"` keeps the announcement a toast used to make, and the
 *    card takes focus so the viewport moves to it — on a phone it can otherwise land
 *    below the fold and read as nothing having happened.
 *
 *  - The comparison table, not a sentence. "The name on this document does not match"
 *    invites argument; two values side by side ends it. It is also the one layout that
 *    survives a long name on a 360px screen without truncating the evidence.
 *
 *  - The tips collapse. They are four lines of advice that only matter for a retake, and
 *    expanded by default they bury the one sentence that says what went wrong.
 */
export default function DocumentStatusCard({ status, onAction, disabled = false }) {
  const cardRef = useRef(null);
  const [showTips, setShowTips] = useState(false);

  // Move the viewport, and the screen reader, to the problem. The card that replaced the
  // toast has to do the job the toast did of interrupting.
  useEffect(() => {
    if (!status || !cardRef.current) return;
    cardRef.current.focus({ preventScroll: true });
    cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [status?.code, status?.title]);

  if (!status) return null;

  const ActionIcon = status.fix === FIX.RETAKE ? ArrowPathIcon : ArrowUpTrayIcon;

  return (
    <div className="mt-3 sm:pl-7">
      <div
        ref={cardRef}
        role="alert"
        tabIndex={-1}
        className="rounded-2xl border border-red-200 bg-red-50/80 p-4 space-y-3 shadow-2xs focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
      >
        <div className="flex items-start gap-2.5">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1 min-w-0 space-y-1">
            <p className="font-bold text-red-900 text-sm leading-snug">{status.title}</p>
            <p className="text-xs text-red-800/90 leading-relaxed">{status.message}</p>
          </div>
        </div>

        {status.comparison && (
          <dl className="rounded-xl bg-white/70 border border-red-200/80 divide-y divide-red-100">
            <div className="px-3 py-2">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-red-700/80">
                {status.comparison.documentLabel}
              </dt>
              <dd className="text-sm font-bold text-red-950 break-words">
                {status.comparison.documentValue || '— could not be read —'}
              </dd>
            </div>
            <div className="px-3 py-2">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {status.comparison.appliedLabel}
              </dt>
              <dd className="text-sm font-bold text-slate-900 break-words">
                {status.comparison.appliedValue}
              </dd>
            </div>
          </dl>
        )}

        {status.tips?.length > 0 && (
          <div className="rounded-xl bg-white/60 border border-red-200/70">
            <button
              type="button"
              onClick={() => setShowTips((v) => !v)}
              aria-expanded={showTips}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-bold text-red-900 cursor-pointer"
            >
              <span>How to take a photo that works</span>
              <ChevronDownIcon
                className={`h-4 w-4 shrink-0 transition-transform ${showTips ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {showTips && (
              <ul className="px-3 pb-3 space-y-1 text-xs text-red-900/90 list-disc pl-7">
                {status.tips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onAction}
          disabled={disabled}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs shadow-2xs transition cursor-pointer"
        >
          <ActionIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {status.actionLabel}
        </button>

        {status.helpNote && (
          <p className="flex items-start gap-1.5 text-[11px] text-red-800/80 leading-relaxed pt-1 border-t border-red-200/70">
            <InformationCircleIcon className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden="true" />
            <span>{status.helpNote}</span>
          </p>
        )}
      </div>
    </div>
  );
}
